
import { updateUserProfile as updateProfile } from "@/services/profileService";
import { platformApiFetch } from '@/services/platformApi/client';
import { toast } from "@/hooks/use-toast";
import { getSupabaseErrorMessage } from "@/utils/supabase/errorMessages";

/**
 * Update user profile
 */
export const updateUserProfile = async (
  userId: string,
  data: { 
    first_name?: string; 
    last_name?: string; 
    email?: string | null;
    avatar_url?: string; 
    whatsapp_number?: string;
  }
): Promise<void> => {
  try {
    if (!userId) throw new Error("Not authenticated");

    const normalizedEmail = typeof data.email === 'string' ? data.email.trim() : null;

    if (normalizedEmail) {
      await platformApiFetch<{ success: boolean; email: string }>('/v1/auth/email', {
        method: 'POST',
        body: JSON.stringify({ email: normalizedEmail }),
      });
    }

    await updateProfile(userId, {
      first_name: data.first_name,
      last_name: data.last_name,
      email: normalizedEmail ?? data.email,
      avatar_url: data.avatar_url,
      whatsapp_number: data.whatsapp_number,
    });
    
    toast({
      title: "Profile updated",
      description: "Your profile has been updated successfully.",
    });
  } catch (error: unknown) {
    const message = getSupabaseErrorMessage(error, 'Unknown error');
    toast({
      title: "Error updating profile",
      description: message,
      variant: "destructive",
    });
    throw error instanceof Error ? error : new Error(message);
  }
};

/**
 * Toggle MFA for user
 */
export const toggleUserMfa = async (userId: string, enable: boolean): Promise<void> => {
  try {
    if (!userId) throw new Error("Not authenticated");

    await updateProfile(userId, {
      mfa_enabled: enable,
    });
    
    toast({
      title: enable ? "MFA Enabled" : "MFA Disabled",
      description: enable 
        ? "Multi-factor authentication has been enabled."
        : "Multi-factor authentication has been disabled.",
    });
  } catch (error: unknown) {
    const message = getSupabaseErrorMessage(error, 'Unknown error');
    toast({
      title: "Error updating MFA",
      description: message,
      variant: "destructive",
    });
    throw error instanceof Error ? error : new Error(message);
  }
};
